import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '../service';

/** Membresía del usuario en un club, con su rol dentro del club */
export interface ClubMembership {
  club_role: 'ADMIN' | 'JUEZ';
  Club: { club_id: number; club_name: string };
}

export interface User {
  idUser: number;
  emailUser: string;
  usernameUser: string;
  fullnameUser: string;
  documentNumberUser: string;
  phoneUser?: string;
  birthDateUser?: string;
  verifiedUser: boolean;
  roleUser?: 'ADMIN' | 'JUEZ' | 'PARTICIPANTE';
  createdAtUser: string;
  updatedAt?: string;
  /** Clubes a los que pertenece, con el rol en cada uno */
  memberships?: ClubMembership[];
}

export const useUserStore = defineStore('user', () => {
  // Estado
  const user = ref<User | null>(null);
  const userModelsCount = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isLoggedIn = computed(() => !!user.value);
  const userDisplayName = computed(() => user.value?.fullnameUser || user.value?.usernameUser || 'Usuario');
  const userEmail = computed(() => user.value?.emailUser || '');
  const userUsername = computed(() => user.value?.usernameUser || '');
  const isVerified = computed(() => user.value?.verifiedUser || false);
  const isAdmin = computed(() => user.value?.roleUser === 'ADMIN');
  /** Membresías de club del usuario */
  const memberships = computed(() => user.value?.memberships ?? []);
  /** Clubes donde el usuario es administrador */
  const adminClubs = computed(() => memberships.value.filter((m) => m.club_role === 'ADMIN').map((m) => m.Club));
  /** Es admin de plataforma o administra al menos un club */
  const isAnyAdmin = computed(() => isAdmin.value || adminClubs.value.length > 0);
  /** Tiene algún rol de administración o de juez (global o de club) */
  const hasAdminAccess = computed(() => user.value?.roleUser !== 'PARTICIPANTE' || memberships.value.length > 0);
  const hasPhone = computed(() => !!(user.value?.phoneUser && user.value.phoneUser.trim() !== ''));

  /**
   * Puede puntuar y premiar los modelos de un concurso (mismo criterio que
   * assertContestJudge en la API): admin o juez de plataforma siempre; si no,
   * debe ser ADMIN o JUEZ del club organizador. Un concurso sin club solo lo
   * juzga la plataforma. Un participante nunca puntúa.
   */
  function canJudgeContest(club_id: number | null | undefined): boolean {
    const role = user.value?.roleUser;
    if (role === 'ADMIN' || role === 'JUEZ') return true;
    if (!club_id) return false;
    return memberships.value.some((m) => m.Club.club_id === club_id);
  }

  /** Administra el concurso: admin de plataforma o ADMIN del club organizador */
  function canAdminContest(club_id: number | null | undefined): boolean {
    if (isAdmin.value) return true;
    if (!club_id) return false;
    return adminClubs.value.some((club) => club.club_id === club_id);
  }

  // Acciones
  async function fetchUserProfile() {
    loading.value = true;
    error.value = null;

    try {
      const { data } = await api.get('/user/profile');
      user.value = data;
    } catch (err: any) {
      error.value = err.message || 'Error al cargar datos del usuario';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchUserModelsCount() {
    try {
      const { data } = await api.get('/model/user/count');
      userModelsCount.value = data;
    } catch (err: any) {
      userModelsCount.value = 0;
    }
  }

  async function loadUserData() {
    try {
      await Promise.all([fetchUserProfile(), fetchUserModelsCount()]);
    } catch (err) {
      // Error silencioso - se maneja en fetchUserProfile
    }
  }

  function clearUser() {
    user.value = null;
    userModelsCount.value = 0;
    error.value = null;
  }

  return {
    // Estado
    user,
    userModelsCount,
    loading,
    error,

    // Getters
    isLoggedIn,
    userDisplayName,
    userEmail,
    userUsername,
    isVerified,
    isAdmin,
    memberships,
    adminClubs,
    isAnyAdmin,
    hasAdminAccess,
    hasPhone,
    canJudgeContest,
    canAdminContest,

    // Acciones
    fetchUserProfile,
    fetchUserModelsCount,
    loadUserData,
    clearUser,
  };
});
